import { SearchInput } from '@/components/searchInput';
import { ROUTESPATH } from '@/routes';
import { REGION_OPTIONS } from '@/utils/constant/appConstant';
import { Button, Popover, Select, Space, Tag } from 'antd';
import React, { useMemo, useState } from 'react';
import { FaShieldAlt } from 'react-icons/fa';
import { IoFunnelOutline, IoCloseOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { TEAM_OPTIONS } from '../constants';
import { useAppRoleList } from '../hooks';

type ViewMode = 'table' | 'cards' | 'permissions-matrix';

interface UserFiltersProps {
  searchQuery: string;
  filterRegion: number;
  filterStatus: number;
  filterTeam: number;
  filterRole: string;
  viewMode: ViewMode;
  onSearchChange: (value: string) => void;
  onRegionChange: (value: number) => void;
  onTeamChange: (value: number) => void;
  onRoleChange: (value: string) => void;
  onStatusChange: (value: number) => void;
  onApplyFilters: (filters: { region: number; team: number; role: string; status: number }) => void;
  onClearFilters: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  isRegionAll?: boolean;
  userRegion?: number;
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 1 },
  { label: 'Deactivated', value: 2 },
];

export const UserFilters: React.FC<UserFiltersProps> = ({
  searchQuery,
  filterRegion,
  filterTeam,
  filterRole,
  filterStatus,
  viewMode,
  onSearchChange,
  onRegionChange,
  onTeamChange,
  onRoleChange,
  onStatusChange,
  onApplyFilters,
  onClearFilters,
  onViewModeChange,
  isRegionAll = true,
  userRegion,
}) => {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch roles from API
  const { data: rolesData, isLoading: isLoadingRoles } = useAppRoleList({
    queryParams: { current: 0, pageSize: 100, filter: "&$filter=dogma_defaultsecurityrole eq true" },
    enabled: true,
  });

  // Convert roles to options format
  const roleOptions = useMemo(() => {
    if (!rolesData?.value) return [];
    return rolesData.value.map((role) => ({
      label: role.dogma_name,
      value: role.dogma_approleid,
    }));
  }, [rolesData]);

  // Filter region options based on user access
  const regionOptions = useMemo(() => {
    if (isRegionAll) return REGION_OPTIONS;
    return REGION_OPTIONS.filter(option => option.value === userRegion);
  }, [isRegionAll, userRegion]);

  // Draft state — only committed when "Apply Filters" is clicked
  const [draftRegion, setDraftRegion] = useState<number>(filterRegion);
  const [draftTeam, setDraftTeam] = useState<number>(filterTeam);
  const [draftRole, setDraftRole] = useState<string>(filterRole);
  const [draftStatus, setDraftStatus] = useState<number>(filterStatus);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftRegion(filterRegion);
      setDraftTeam(filterTeam);
      setDraftRole(filterRole);
      setDraftStatus(filterStatus);
    }
    setFilterPopoverOpen(open);
  };

  const handleApplyFilters = () => {
    onApplyFilters({ region: draftRegion, team: draftTeam, role: draftRole, status: draftStatus });
    setFilterPopoverOpen(false);
  };

  const handleClearAll = () => {
    setDraftRegion(0);
    setDraftTeam(0);
    setDraftRole('');
    setDraftStatus(0);
    onClearFilters();
  };

  // Helper function to get filter label
  const getFilterLabel = (type: string, value: number | string) => {
    if (!value || value === 0 || value === '') return null;
    switch (type) {
      case 'region':
        return REGION_OPTIONS.find(opt => opt.value === value)?.label;
      case 'team':
        return TEAM_OPTIONS.find(opt => opt.value === value)?.label;
      case 'role':
        return roleOptions.find(opt => opt.value === value)?.label;
      case 'status':
        return STATUS_OPTIONS.find(opt => opt.value === value)?.label;
      default:
        return null;
    }
  };

  // Count active applied filters
  const activeFilterCount = [
    filterRole !== '' && filterRole !== undefined,
    isRegionAll && filterRegion !== 0 && filterRegion !== undefined,
    filterTeam !== 0 && filterTeam !== undefined,
    filterStatus !== 0 && filterStatus !== undefined,
  ].filter(Boolean).length;

  return (
    <div className="mb-4">
      <div className="flex gap-3 items-center flex-wrap justify-between">
        {/* Left side: Search and Filters */}
        <div className="flex gap-3 items-center flex-wrap flex-1">
          {/* Search */}
          <div className="flex-1 min-w-[250px] max-w-[500px]">
            <SearchInput
              placeholder="Search by name, email, or domain..."
              onChange={onSearchChange}
              allowClear
            />
          </div>

          {/* More Filters Button */}
          <div>
            <Popover
              content={
                <div style={{ width: 380 }}>
                  <div
                    style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}
                    className="pr-2"
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select status"
                          value={draftStatus === 0 ? undefined : draftStatus}
                          onChange={(value) => {
                            setDraftStatus(value === undefined || value === null ? 0 : value as number);
                          }}
                          options={STATUS_OPTIONS}
                          allowClear
                        />
                      </div>

                      {/* Role Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Role
                        </label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select role"
                          value={draftRole === '' ? undefined : draftRole}
                          onChange={(value) => {
                            setDraftRole(value === undefined || value === null ? '' : value as string);
                          }}
                          options={roleOptions}
                          loading={isLoadingRoles}
                          allowClear
                        />
                      </div>

                      {/* Region Filter */}
                      {isRegionAll && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Region
                          </label>
                          <Select
                            style={{ width: '100%' }}
                            placeholder="Select region"
                            value={draftRegion === 0 ? undefined : draftRegion}
                            onChange={(value) => {
                              setDraftRegion(value === undefined || value === null ? 0 : value as number);
                            }}
                            options={regionOptions}
                            allowClear
                          />
                        </div>
                      )}

                      {/* Team Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Team
                        </label>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select team"
                          value={draftTeam === 0 ? undefined : draftTeam}
                          onChange={(value) => {
                            setDraftTeam(value === undefined || value === null ? 0 : value as number);
                          }}
                          options={TEAM_OPTIONS}
                          allowClear
                        />
                      </div>
                    </Space>
                  </div>

                  {/* Footer */}
                  <div
                    className="pt-3 mt-3 flex flex-row justify-end"
                    style={{ borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}
                  >
                    <Space style={{ width: '100%' }}>
                      {activeFilterCount > 0 && (
                        <Button block onClick={handleClearAll}>
                          Clear All
                        </Button>
                      )}
                      <Button type="primary" block onClick={handleApplyFilters}>
                        Apply Filters
                      </Button>
                    </Space>
                  </div>
                </div>
              }
              title={
                <div className="flex items-center justify-between">
                  <span>Filter Options</span>
                  {activeFilterCount > 0 && (
                    <span className="text-xs text-gray-500">{activeFilterCount} active</span>
                  )}
                </div>
              }
              trigger="click"
              open={filterPopoverOpen}
              onOpenChange={handleOpenChange}
              placement="bottomRight"
            >
              <Button icon={<IoFunnelOutline />} className='h-[44px]'>
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-600 rounded-full text-xs font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </Popover>
          </div>
        </div>

        {/* Right side: Action Buttons */}
        <div>
          <Button
            icon={<FaShieldAlt />}
            onClick={() => navigate(ROUTESPATH.PERMISSIONS)}
            className='h-[44px]'
          >
            View Permissions Matrix
          </Button>
        </div>
      </div>

      {/* Active Filters Tags */}
      {activeFilterCount > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {filterRole !== '' && filterRole !== undefined && (
            <Tag closable color={"#dc3b41"} onClose={() => onRoleChange('')}>
              Role: {getFilterLabel('role', filterRole)}
            </Tag>
          )}
          {isRegionAll && filterRegion !== 0 && filterRegion !== undefined && (
            <Tag closable color={"#dc3b41"} onClose={() => onRegionChange(0)}>
              Region: {getFilterLabel('region', filterRegion)}
            </Tag>
          )}
          {filterTeam !== 0 && filterTeam !== undefined && (
            <Tag closable color={"#dc3b41"} onClose={() => onTeamChange(0)}>
              Team: {getFilterLabel('team', filterTeam)}
            </Tag>
          )}
          {filterStatus !== 0 && filterStatus !== undefined && (
            <Tag closable color={"#dc3b41"} onClose={() => onStatusChange(0)}>
              Status: {getFilterLabel('status', filterStatus)}
            </Tag>
          )}
          <Button
            type="link"
            size="small"
            className='text-primary-500'
            icon={<IoCloseOutline />}
            onClick={onClearFilters}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserFilters;
